// src/screens/Marketplace/EditProduct.js
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../api';
import './AddProduct.css'; // reuse styles (pills, inputs, footer, etc.)

const DELIVERY_OPTIONS = [
  'PEP (PAXI)',
  'PostNet',
  'The Courier Guy',
  'Aramex',
  'DHL',
  'FedEx',
  'UPS',
  'Fastway',
  'Local Pickup',
  'Meet-up (in person)',
  "Seller's Own Courier",
  'Electronic Delivery',
  'Other'
];

// 🆕 Departments (keys -> we submit the label to backend)
const DEPARTMENTS = [
  { key: 'clothing',     label: 'Clothing & Accessories',  emoji: '👕' },
  { key: 'jewelry',      label: 'Jewelry & Watches',       emoji: '💍' },
  { key: 'toys',         label: 'Toys & Games',            emoji: '🧸' },
  { key: 'electronics',  label: 'Electronics & Gadgets',   emoji: '📱' },
  { key: 'home',         label: 'Home & Living',           emoji: '🛋️' },
  { key: 'books',        label: 'Books & Stationery',      emoji: '📚' },
  { key: 'art',          label: 'Art & Collectibles',      emoji: '🎨' },
  { key: 'essentials',   label: 'Health & Essentials',     emoji: '🧴' },
  { key: 'sports',       label: 'Sports & Outdoors',       emoji: '⚽' },
  { key: 'luggage',      label: 'Luggage & Travel',        emoji: '🧳' },
  { key: 'courses',      label: 'Courses & E-Books',       emoji: '🎓' },
  { key: 'gaming',       label: 'Gaming Items',            emoji: '🎮' },
  { key: 'vouchers',     label: 'Subscriptions & Vouchers',emoji: '🎟️' },
  { key: 'nft',          label: 'NFTs (Soon)',             emoji: '🖼️' },
  { key: 'other',        label: 'Other',                   emoji: '➕' }
];

function EditProduct({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);

  // pricing + description
  const [pricingType, setPricingType] = useState('raw');
  const [price, setPrice] = useState('');
  const [description, setDescription] = useState('');

  // NEW: estimated delivery days (editable)
  const [expectedDeliveryDays, setExpectedDeliveryDays] = useState('');

  // NEW: multiple delivery selections
  const [selectedDelivery, setSelectedDelivery] = useState([]); // array of strings (no "Other")
  const [deliveryIsOther, setDeliveryIsOther] = useState(false);
  const [deliveryOtherText, setDeliveryOtherText] = useState('');

  // 🆕 Department state
  const [departmentKey, setDepartmentKey] = useState('other');

  const [loading, setLoading] = useState(false);

  const toggleDelivery = (opt) => {
    // handle "Other" separately
    if (opt === 'Other') {
      setDeliveryIsOther((prev) => !prev);
      if (deliveryIsOther) {
        // turning off Other clears text
        setDeliveryOtherText('');
      }
      return;
    }

    setSelectedDelivery((prev) =>
      prev.includes(opt) ? prev.filter((v) => v !== opt) : [...prev, opt]
    );
  };

  const labelToKey = (label) => {
    const f = DEPARTMENTS.find(d => d.label === label);
    return f ? f.key : 'other';
  };
  const keyToLabel = (key) => {
    const f = DEPARTMENTS.find(d => d.key === key);
    return f ? f.label : 'Other';
  };

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const res = await api.get(`/products/${id}`);
        const data = res.data?.product; // API returns { product }
        if (!data) throw new Error('No product payload');

        setProduct(data);
        setDescription(data.description || '');
        setPricingType(data.pricingType || 'raw');
        setPrice((data.pricingType === 'dynamic' ? data.usdPrice : data.price) ?? '');

        // init estimated delivery days
        setExpectedDeliveryDays(
          data.expectedDeliveryDays != null ? String(data.expectedDeliveryDays) : ''
        );

        // init department (string label from backend → key)
        setDepartmentKey(labelToKey(data.department || 'Other'));

        // ---- Normalize delivery (supports new+old shapes) ----
        const existingList = Array.isArray(data.deliveryMethods)
          ? data.deliveryMethods
          : Array.isArray(data.deliveryMethod)
            ? data.deliveryMethod
            : typeof data.deliveryMethod === 'string' && data.deliveryMethod.trim()
              ? [data.deliveryMethod.trim()]
              : [];

        // separate known options from any custom text (old records sometimes stored custom in deliveryMethod)
        const known = existingList.filter((opt) => DELIVERY_OPTIONS.includes(opt) && opt !== 'Other');

        const customText =
          (typeof data.customOtherDelivery === 'string' && data.customOtherDelivery.trim()) ||
          existingList.find((opt) => !DELIVERY_OPTIONS.includes(opt)) ||
          '';

        setSelectedDelivery(known);
        setDeliveryIsOther(Boolean(customText) || existingList.includes('Other'));
        setDeliveryOtherText(customText || '');
      } catch (err) {
        console.error('❌ Failed to load product:', err);
        alert('Failed to load product');
        navigate(-1);
      }
    };

    fetchProduct();
  }, [id, navigate]);

  const isDynamic = useMemo(() => pricingType === 'dynamic', [pricingType]);

  const handleSave = async () => {
    // Validate delivery
    const cleanDelivery = selectedDelivery.filter((m) => m !== 'Other');
    if (cleanDelivery.length === 0 && !deliveryIsOther) {
      alert('Please select at least one delivery option or add an Other method.');
      return;
    }
    if (deliveryIsOther && !deliveryOtherText.trim()) {
      alert('Please type your "Other" delivery method.');
      return;
    }

    // Validate expected delivery days
    if (!expectedDeliveryDays) {
      alert('Please select an estimated delivery time.');
      return;
    }

    // Prepare department label for backend enum
    const departmentLabel = keyToLabel(departmentKey);

    setLoading(true);
    try {
      await api.put(`/products/${id}`, {
        pricingType,
        price: !isDynamic ? Number(price) : undefined,
        usdPrice: isDynamic ? Number(price) : undefined,
        description,
        expectedDeliveryDays: Number(expectedDeliveryDays),

        // 🆕 Department (server will validate and fallback to "Other" if needed)
        department: departmentLabel,

        // NEW payload (matches backend normalization)
        deliveryMethods: cleanDelivery,                           // array of strings (without "Other")
        customOtherDelivery: deliveryIsOther ? deliveryOtherText.trim() : ''
      });

      navigate(`/product/${id}`, { replace: true });
    } catch (err) {
      console.error('❌ Update failed:', err);
      alert('Failed to update product');
    } finally {
      setLoading(false);
    }
  };

  if (!product) return null;

  return (
    <div className="addproduct-fullscreen">
      <div className="addproduct-header">Edit Product</div>

      <form className="addproduct-content editproduct-scroll-content" onSubmit={(e) => e.preventDefault()}>
        {/* Title (locked) */}
        <label>Product Title</label>
        <input value={product.name} disabled style={{ background: '#eee' }} />

        {/* Pricing */}
        <label>Pricing Type</label>
        <select
          value={pricingType}
          onChange={(e) => {
            setPricingType(e.target.value);
            setPrice('');
          }}
        >
          <option value="raw">Raw Pi</option>
          <option value="dynamic">Dynamic Pricing</option>
        </select>

        <label>{isDynamic ? 'Price (in USD)' : 'Price (in Pi)'}</label>
        <input
          type="number"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
        />

        {/* Category (locked) */}
        <label>Category</label>
        <input value={product.category} disabled style={{ background: '#eee' }} />

        {/* 🆕 Department (editable, under Category) */}
        <label>Department</label>
        <select
          value={departmentKey}
          onChange={(e) => setDepartmentKey(e.target.value)}
          required
        >
          {DEPARTMENTS.map((d) => (
            <option key={d.key} value={d.key}>
              {d.emoji} {d.label}
            </option>
          ))}
        </select>

        {/* Condition (read-only to keep original behavior) */}
        <label>Condition</label>
        <div className="addproduct-pill-group">
          <button
            type="button"
            className={`addproduct-pill ${product.condition === 'new' ? 'active' : ''}`}
            disabled
            style={{ opacity: 0.6, cursor: 'not-allowed' }}
          >
            New
          </button>
          <button
            type="button"
            className={`addproduct-pill ${product.condition === 'used' ? 'active' : ''}`}
            disabled
            style={{ opacity: 0.6, cursor: 'not-allowed' }}
          >
            Used
          </button>
        </div>

        {/* Description */}
        <label>Description</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={5}
        />

        {/* Images (unchangeable) */}
        <label>Images (unchangeable)</label>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {product.media?.map((url, index) => (
            <img
              key={index}
              src={url}
              alt={`preview-${index}`}
              style={{ width: 60, height: 60, borderRadius: 8, objectFit: 'cover' }}
            />
          ))}
        </div>

        {/* Estimated Delivery Time (editable) */}
        <label>Estimated Delivery Time</label>
        <select
          value={expectedDeliveryDays}
          onChange={(e) => setExpectedDeliveryDays(e.target.value)}
          required
        >
          <option value="">Select delivery time</option>
          <option value="3">3 Days</option>
          <option value="7">7 Days</option>
          <option value="14">14 Days</option>
          <option value="21">21 Days</option>
          <option value="30">30 Days</option>
        </select>

        {/* Delivery Methods (multiple) */}
        <label>Delivery Method(s)</label>
        <div className="addproduct-pill-group" style={{ flexWrap: 'wrap' }}>
          {DELIVERY_OPTIONS.filter((o) => o !== 'Other').map((opt) => (
            <button
              key={opt}
              type="button"
              className={`addproduct-pill ${selectedDelivery.includes(opt) ? 'active' : ''}`}
              onClick={() => toggleDelivery(opt)}
            >
              {opt}
            </button>
          ))}

          {/* "Other" pill */}
          <button
            type="button"
            className={`addproduct-pill ${deliveryIsOther ? 'active' : ''}`}
            onClick={() => toggleDelivery('Other')}
          >
            Other
          </button>
        </div>

        {deliveryIsOther && (
          <input
            className="mt8"
            value={deliveryOtherText}
            onChange={(e) => setDeliveryOtherText(e.target.value)}
            placeholder="Type your delivery method (e.g., Mr D, WhatsApp file, In-person at Wits Library)"
          />
        )}
      </form>

      <div className="addproduct-footer">
        <button onClick={() => navigate(-1)}>Cancel</button>
        <button onClick={handleSave} disabled={loading}>
          {loading ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}

export default EditProduct;
